import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth } from '../hooks/useAuth';
import { MatterProvider } from '../hooks/useMatter';

interface AuthGuardProps {
  children: ReactNode;
}

/**
 * Wraps protected routes. While Supabase rehydrates the session, render a tiny
 * loading state. If there's no user once loading completes, redirect to /login.
 *
 * Once authenticated, fetch the user's matters via <MatterProvider> so all
 * downstream pages can read the active matter without each refetching.
 */
export default function AuthGuard({ children }: AuthGuardProps) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50">
        <div className="text-sm text-slate-500">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  return <MatterProvider>{children}</MatterProvider>;
}
